import { useSelector } from 'react-redux';

import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { AdminRoute } from 'app/route/admin';
import { ClientRoute } from 'app/route/client';
import { FeatureFlags } from 'config/featureFlags';
import {
  selectIsAdmin,
  selectIsClientAdmin,
  selectIsGlobalQuestionLibraryEditor,
} from 'reduxStore/auth/selectors';
import { wbLazy } from '@workbuzz/shared-ui';
import { ClientsPage } from 'view/ClientsPage/ClientsPage';
import { ProjectsListPage } from 'view/ProjectsListPage/ProjectsListPage';

import { NavigationItem } from './models';

const LibraryRoutes = wbLazy(() =>
  import(/* webpackChunkName: "LibraryRoutes" */ 'router/LibraryRoutes/LibraryRoutes').then(
    (module) => ({
      default: module['LibraryRoutes'],
    }),
  ),
);
const ProtectedPulseProjectRoutes = wbLazy(() =>
  import(
    /* webpackChunkName: "ProtectedPulseProjectRoutes" */ 'router/PulseProjectRoutes/ProtectedPulseProjectRoutes'
  ).then((module) => ({
    default: module['ProtectedPulseProjectRoutes'],
  })),
);
const ProtectedRegularProjectRoutes = wbLazy(() =>
  import(
    /* webpackChunkName: "ProtectedRegularProjectRoutes" */ 'router/RegularProjectRoutes/ProtectedRegularProjectRoutes'
  ).then((module) => ({
    default: module['ProtectedRegularProjectRoutes'],
  })),
);
const LibraryPage = wbLazy(() =>
  import(/* webpackChunkName: "LibraryPage" */ 'view/LibraryPage/LibraryPage').then((module) => ({
    default: module['LibraryPage'],
  })),
);
const PulseSurveyTemplatesPage = wbLazy(() =>
  import(
    /* webpackChunkName: "PulseSurveyTemplatesPage" */ 'view/PulseSurveyTemplatesPage/PulseSurveyTemplatesPage'
  ).then((module) => ({
    default: module['PulseSurveyTemplatesPage'],
  })),
);
const GlobalQuestionLibraryPage = wbLazy(() =>
  import(
    /* webpackChunkName: "GlobalQuestionLibraryPage" */ 'view/GlobalQuestionLibraryPage/GlobalQuestionLibraryPage'
  ).then((module) => ({
    default: module['GlobalQuestionLibraryPage'],
  })),
);

export const useAppRoutes = (): NavigationItem[] => {
  const isClientAdmin = useSelector(selectIsClientAdmin);
  const isAdmin = useSelector(selectIsAdmin);
  const isTemplateLibraryUploadEnabled = useFeatureIsOn(FeatureFlags.TEMPLATE_LIBRARY_UPLOAD);
  const isGlobalQuestionLibraryEnabled = useFeatureIsOn(FeatureFlags.GLOBAL_QUESTION_LIBRARY);
  const isGlobalQuestionLibraryEditor = useSelector(selectIsGlobalQuestionLibraryEditor);

  return [
    {
      path: ClientRoute.PROJECT_PATTERN,
      component: ProtectedPulseProjectRoutes,
      canActivate: isClientAdmin || isAdmin,
    },
    {
      path: AdminRoute.PROJECT_TEMPLATE_PATTERN,
      component: LibraryRoutes,
      canActivate: isClientAdmin || isAdmin,
    },
    {
      path: AdminRoute.PROJECT_TEMPLATES_PATTERN,
      component: LibraryPage,
      canActivate: isClientAdmin || isAdmin,
    },
    {
      path: AdminRoute.PULSE_SURVEY_TEMPLATES_PATTERN,
      component: PulseSurveyTemplatesPage,
      canActivate: isTemplateLibraryUploadEnabled && (isClientAdmin || isAdmin),
    },
    {
      path: AdminRoute.GLOBAL_QUESTION_LIBRARY_PATTERN,
      component: GlobalQuestionLibraryPage,
      canActivate: isGlobalQuestionLibraryEnabled && isGlobalQuestionLibraryEditor,
    },
    {
      path: AdminRoute.PROJECT_PATTERN,
      component: ProtectedRegularProjectRoutes,
      canActivate: isAdmin,
    },
    {
      path: AdminRoute.PROJECTS_PATTERN,
      component: ProjectsListPage,
      canActivate: isAdmin,
    },
    {
      path: AdminRoute.CLIENTS_PATTERN,
      component: ClientsPage,
      canActivate: isAdmin,
    },
  ];
};
